/**
 * Order packed email — line list HTML/text, shipping label from Firestore order,
 * and chilled vs non-chilled messaging (salmon/sausage vs packed goods only).
 */

import unifiedProductService from '../services/unifiedProductService';
import { getCartItemImageUrlFromItem } from './cartEmailPreview';
import {
    EMAIL_BODY_FONT_STACK,
    EMAIL_TEXT_PRIMARY,
    EMAIL_TEXT_MUTED,
    EMAIL_BORDER_LIGHT,
    EMAIL_ACCENT_GOLD,
    EMAIL_PRE_BG,
    EMAIL_HEADING_COLOR,
    wrapTransactionalEmailHtml,
} from './emailBrandStyles';

function escapeHtml(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * Normalise Firestore / OrderDesk lines to a common shape for packed email.
 */
export function normalizeOrderLinesForPackedEmail(order) {
    const raw = Array.isArray(order?.order_items)
        ? order.order_items
        : Array.isArray(order?.items)
          ? order.items
          : [];
    return raw
        .map((item) => ({
            name: item.name || item.title || item.description || '',
            sku: String(item.code || item.sku || item.SKU || '').trim(),
            quantity: Number(item.quantity ?? item.qty ?? item.amount ?? 0) || 0,
            imageUrl: getCartItemImageUrlFromItem(item),
            category: item.category || '',
        }))
        .filter((row) => row.quantity > 0 && (row.name || row.sku));
}

function categorySignalsForLine(line) {
    const fromLine = String(line.category || '').toLowerCase();
    if (fromLine.includes('salmon')) return { salmon: true, sausage: false };
    if (fromLine.includes('sausage')) return { salmon: false, sausage: true };

    const fromCatalog = String(unifiedProductService.getProductCategory(line.sku) || '').toLowerCase();
    const name = String(line.name || '').toLowerCase();
    const salmon = fromCatalog.includes('salmon') || name.includes('salmon');
    const sausage = fromCatalog.includes('sausage') || name.includes('sausage');
    return { salmon, sausage };
}

/**
 * True if the order needs the chilled / ice delivery note (salmon or sausage lines only).
 */
export function orderPackedNeedsChilledNote(lines) {
    for (const line of lines) {
        const { salmon, sausage } = categorySignalsForLine(line);
        if (salmon || sausage) return true;
    }
    return false;
}

/**
 * HTML fragment: ice callout when salmon/sausage; otherwise a short “no chilled goods” line.
 */
export function buildChilledProductNoteHtml(lines) {
    if (orderPackedNeedsChilledNote(lines)) {
        return (
            `<div style="margin:16px 0;padding:14px 16px;background:${EMAIL_PRE_BG};border-left:4px solid ${EMAIL_ACCENT_GOLD};border-radius:0 8px 8px 0;">` +
            `<p style="margin:0;font-family:${EMAIL_BODY_FONT_STACK};font-size:14px;line-height:1.55;color:${EMAIL_TEXT_PRIMARY};">` +
            `This order includes chilled salmon and/or fresh sausage. We pack these with ice for the journey. ` +
            `Please try to be in to take delivery, or arrange for someone to receive the parcel on your behalf—` +
            `chilled goods should go straight into the fridge.` +
            `</p></div>`
        );
    }
    return (
        `<div style="margin:12px 0;padding:12px 14px;background:${EMAIL_PRE_BG};border-radius:8px;border:1px solid ${EMAIL_BORDER_LIGHT};">` +
        `<p style="margin:0;font-family:${EMAIL_BODY_FONT_STACK};font-size:14px;line-height:1.55;color:${EMAIL_TEXT_MUTED};">` +
        `This parcel has no chilled salmon or fresh sausage — packed goods only (no ice pack required).` +
        `</p></div>`
    );
}

/** Plain-text counterpart for multipart email body. */
export function buildChilledProductNotePlainText(lines) {
    if (orderPackedNeedsChilledNote(lines)) {
        return (
            'This order includes chilled salmon and/or fresh sausage. We pack these with ice for the journey. ' +
            'Please try to be in to take delivery, or arrange for someone to receive the parcel on your behalf—' +
            'chilled goods should go straight into the fridge.'
        );
    }
    return 'This parcel has no chilled salmon or fresh sausage — packed goods only (no ice pack required).';
}

/** Plain body (merge placeholders) — single source for transporter + Template Studio defaults. */
export function defaultOrderPackedTemplateText() {
    return `Hi --customerName--,

Your order --orderId-- has just been packed and is ready for dispatch.
Packed on: --packedAt--

Delivery: --shippingMethod--

--chilledProductNoteText--

Items:
--itemsText--

We’ll be shipping your order soon. Thank you for choosing London Smoke & Cure!`;
}

/** HTML with logo + standard footer via wrapTransactionalEmailHtml; inner uses merge placeholders. */
export function defaultOrderPackedTemplateHtml() {
    const inner =
        `<h2 style="color:${EMAIL_HEADING_COLOR};margin-top:0;font-size:22px;">Order packed</h2>` +
        `<p style="margin:12px 0;font-family:${EMAIL_BODY_FONT_STACK};">Hi --customerName--,</p>` +
        `<p style="margin:12px 0;font-family:${EMAIL_BODY_FONT_STACK};">Your order <strong>#--orderId--</strong> has just been packed and is ready for dispatch.</p>` +
        `<div style="margin:16px 0;padding:12px 14px;background:${EMAIL_PRE_BG};border-left:4px solid ${EMAIL_ACCENT_GOLD};border-radius:0 8px 8px 0;font-family:${EMAIL_BODY_FONT_STACK};">` +
        `<strong>Packed on:</strong> <em>--packedAt--</em>` +
        `</div>` +
        `<p style="margin:12px 0 8px 0;font-family:${EMAIL_BODY_FONT_STACK};">Delivery: <strong>--shippingMethod--</strong></p>` +
        `--chilledProductNote--` +
        `--itemsHtml--` +
        `<p style="margin:18px 0 0 0;font-size:14px;color:${EMAIL_TEXT_MUTED};font-family:${EMAIL_BODY_FONT_STACK};">We’ll be shipping your order soon.</p>`;

    return wrapTransactionalEmailHtml(inner);
}

/**
 * Shipping / fulfilment method from the stored order (same field as OrderDesk sync / checkout).
 */
export function getShippingMethodLabelFromOrder(order) {
    const m =
        order?.shipping_method ||
        order?.shippingMethod ||
        order?.['Shipping Method'] ||
        '';
    const t = String(m).trim();
    return t || 'As arranged for your account';
}

/**
 * Compact line list for email (smaller images than cart nudge preview — fits larger orders).
 * @param {Array<{name:string,sku:string,quantity:number,imageUrl:string|null}>} lines
 * @param {number} thumbPx default 72
 */
export function buildOrderPackedItemsHtml(lines, thumbPx = 72) {
    if (!lines.length) {
        return `<p style="margin:8px 0;color:${EMAIL_TEXT_MUTED};font-family:${EMAIL_BODY_FONT_STACK};font-size:14px;">We could not load line items for this order; if anything looks wrong, contact us and quote your order number.</p>`;
    }

    const rows = lines.map((line) => {
        const name = line.name || line.sku || 'Item';
        const safeName = escapeHtml(name);
        const safeSku = line.sku ? escapeHtml(line.sku) : '';
        const qty = line.quantity;
        const skuLine = safeSku
            ? `<div style="font-family:${EMAIL_BODY_FONT_STACK};font-size:12px;color:${EMAIL_TEXT_MUTED};margin:0 0 4px 0;">${safeSku}</div>`
            : '';
        const imgUrl = line.imageUrl ? escapeHtml(line.imageUrl) : '';
        const imgCell = line.imageUrl
            ? `<td width="${thumbPx}" valign="middle" style="width:${thumbPx}px;padding:8px 10px 8px 0;vertical-align:middle;"><img src="${imgUrl}" alt="${safeName}" width="${thumbPx}" height="${thumbPx}" style="display:block;width:${thumbPx}px;height:${thumbPx}px;object-fit:cover;border:0;border-radius:6px;background:${EMAIL_PRE_BG};" /></td>`
            : `<td width="${thumbPx}" style="width:${thumbPx}px;padding:8px 10px 8px 0;vertical-align:middle;"><div style="width:${thumbPx}px;height:${thumbPx}px;background:${EMAIL_PRE_BG};border:1px solid ${EMAIL_BORDER_LIGHT};border-radius:6px;">&nbsp;</div></td>`;

        return (
            `<tr>` +
            imgCell +
            `<td valign="middle" style="padding:8px 0;vertical-align:middle;font-family:${EMAIL_BODY_FONT_STACK};">` +
            `<div style="font-size:15px;font-weight:600;color:${EMAIL_TEXT_PRIMARY};line-height:1.35;margin:0 0 4px 0;">${safeName}</div>` +
            skuLine +
            `<div style="font-size:13px;font-weight:600;color:${EMAIL_ACCENT_GOLD};">Qty: ${qty}</div>` +
            `</td></tr>`
        );
    }).join('');

    return (
        `<div style="margin:12px 0 8px 0;">` +
        `<p style="margin:0 0 10px 0;font-family:${EMAIL_BODY_FONT_STACK};font-size:15px;font-weight:600;color:${EMAIL_TEXT_PRIMARY};">What’s in this parcel</p>` +
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:560px;border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;">` +
        rows +
        `</table></div>`
    );
}

export function buildOrderPackedItemsPlainText(lines) {
    if (!lines.length) return '(Line items not available)';
    return lines
        .map((line) => {
            const qty = line.quantity;
            const sku = line.sku;
            const name = line.name || sku || 'Item';
            const label = name + (sku && name !== sku ? ` (${sku})` : '');
            return `${qty}× ${label}`;
        })
        .join('\n');
}
